import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, useSidebar } from "@/ds/shadcn/components/ui/sidebar";
import { Bell, BellIcon, BoxIcon, ChartNoAxesCombined, CircleStar, CreditCardIcon, CrownIcon, EllipsisVerticalIcon, Gauge, LockIcon, LogOutIcon, Megaphone, QrCodeIcon, Scale, Search, TagIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, type FC } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import icon from "@/assets/icon.svg";
import discord from "@/assets/discord.svg";
import { useAuthState } from "@/lib/state";
import { usePushAppLoad } from "@/features/common/api/usePushAppLoad";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/ds/shadcn/components/ui/dropdown-menu";
import { screenSizeIsConsideredDesktop, screenSizeIsConsideredMobile, useScreenSize } from "@/lib/common/ScreenSizeContext";
import { usePreferredCurrency } from "@/features/common/api/usePreferredCurrency";
import { useSetPreferredCurrency } from "@/features/common/api/useSetPreferredCurrency";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { CurrencyOptions, type Currency } from "@/lib/domain/Currency";
import { usePreferredPriceSource } from "@/features/common/api/usePreferredPriceSource";
import { useSetPreferredPriceSource } from "@/features/common/api/useSetPreferredPriceSource";
import { PriceSourceOptions, type PriceSource } from "@/lib/domain/PriceSource";
import { useEmblem } from "@/features/common/api/useEmblem";
import { Emblem } from "@/features/common/components/Emblem";
import { Selector } from "@/ds/custom/Selector";
import { useNotificationsUnreadCount } from "@/features/notifications/api/useNotificationsUnreadCount";
import { Badge } from "@/ds/shadcn/components/ui/badge";
import { Button } from "@/ds/shadcn/components/ui/button";
import { useUserPreferencesState } from "@/lib/state/UserPreferencesState";
import { OptionBar } from "@/ds/custom/OptionBar";
import { ViewTypeOptions } from "@/lib/common/ViewType";
import { HeaderBarProvider } from "@/features/common/components/HeaderBarProvider";
import dayjs from "dayjs";

export const FrameDashboard: FC = () => {
	const { state: authState, dispatchAuthSetPostLoginRedirectURL, dispatchAuthLoggedOut } = useAuthState();
	const navigate = useNavigate();
	const location = useLocation();
	const appLoadEvent = usePushAppLoad();
	const screenSize = useScreenSize();
	const emblem = useEmblem();
	const { state: userPreferencesState, dispatchSetViewTypePreference } = useUserPreferencesState();

	const preferredCurrency = usePreferredCurrency();
	const setPreferredCurrency = useSetPreferredCurrency();
	const preferredCurrencyValue = useMemo(() => preferredCurrency.data?.currency ?? "USD", [preferredCurrency.data?.currency]);
	const onChangePreferredCurrency = useCallback((currency: Currency) => setPreferredCurrency.mutate({ currency }), [setPreferredCurrency]);
	const isPreferredCurrencyLoading = useMemo(() => preferredCurrency.isLoading || setPreferredCurrency.isPending, [preferredCurrency.isLoading, setPreferredCurrency.isPending]);

	const preferredPriceSource = usePreferredPriceSource();
	const setPreferredPriceSource = useSetPreferredPriceSource();
	const preferredPriceSourceValue = useMemo(() => preferredPriceSource.data?.priceSource ?? "TcgPlayerMarket", [preferredPriceSource.data?.priceSource]);
	const onChangePreferredPriceSource = useCallback((priceSource: PriceSource) => setPreferredPriceSource.mutate({ priceSource }), [setPreferredPriceSource]);
	const isPreferredPriceSourceLoading = useMemo(() => preferredPriceSource.isLoading || setPreferredPriceSource.isPending, [preferredPriceSource.isLoading, setPreferredPriceSource.isPending]);
	const notificationsUnreadCount = useNotificationsUnreadCount();

	const totalUnreadCount = notificationsUnreadCount.data ? notificationsUnreadCount.data.unreadCount : 0;

	useEffect(() => appLoadEvent.mutate({
		attribution: authState.attribution || null
	}), []);

	useEffect(() => {
		if (authState.accessToken) {
			return;
		}

		const { pathname, search, hash } = window.location;
		dispatchAuthSetPostLoginRedirectURL(pathname + search + hash);
		navigate("/login");
	}, [authState.accessToken]);

	const getMenuItemClass = (href: string): string => {
		return location.pathname.startsWith(`/${href}`) ? "bg-primary text-white hover:text-white hover:bg-primary" : "";
	};

	return (
		<SidebarProvider>
			<CloseMobileSidebarOnNavigate />
			<Sidebar className="z-20">
				<SidebarHeader>
					<div className="flex justify-center p-2">
						<img src={icon} className="h-6 w-6" />
					</div>
				</SidebarHeader>
				<SidebarContent>
					<SidebarGroup>
						<SidebarGroupContent>
							<SidebarMenu>
								<SidebarMenuItem>
									<Link to="/dashboard">
										<SidebarMenuButton className={getMenuItemClass("dashboard")}>
											<Gauge />
											Dashboard
										</SidebarMenuButton>
									</Link>
								</SidebarMenuItem>
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
					<SidebarGroup>
						<SidebarGroupLabel>Products</SidebarGroupLabel>
						<SidebarGroupContent>
							<SidebarMenu>
								<SidebarMenuItem>
									<Link to="/collections">
										<SidebarMenuButton className={getMenuItemClass("collections")}>
											<BoxIcon />
											Collections
										</SidebarMenuButton>
									</Link>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<Link to="/browse">
										<SidebarMenuButton className={getMenuItemClass("browse")}>
											<Search />
											Browse
										</SidebarMenuButton>
									</Link>
								</SidebarMenuItem>
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
					<SidebarGroup>
						<SidebarGroupLabel>Data</SidebarGroupLabel>
						<SidebarGroupContent>
							<SidebarMenu>
								<SidebarMenuItem>
									<Link to="/market-trends">
										<SidebarMenuButton className={getMenuItemClass("market-trends")}>
											<ChartNoAxesCombined />
											Market Trends
										</SidebarMenuButton>
									</Link>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<Link to="/ev-tracker">
										<SidebarMenuButton className={getMenuItemClass("ev-tracker")}>
											<Scale />
											EV Tracker
										</SidebarMenuButton>
									</Link>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<Link to="/price-alerts">
										<SidebarMenuButton className={getMenuItemClass("price-alerts")}>
											<Megaphone />
											Price Alerts
										</SidebarMenuButton>
									</Link>
								</SidebarMenuItem>
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
					<SidebarGroup>
						<SidebarGroupLabel>Other</SidebarGroupLabel>
						<SidebarGroupContent>
							<SidebarMenu>
								<SidebarMenuItem>
									<Link to="/emblems">
										<SidebarMenuButton className={getMenuItemClass("emblems")}>
											<CircleStar />
											Emblems
										</SidebarMenuButton>
									</Link>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<Link to="/notifications">
										<SidebarMenuButton className={getMenuItemClass("notifications")}>
											<Bell />

											<div className="grow">Notifications</div>

											{
												totalUnreadCount > 0 && (
													<Badge variant="default">{totalUnreadCount > 99 ? 99 : totalUnreadCount}</Badge>
												)
											}
										</SidebarMenuButton>
									</Link>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<Link to={"/price-overrides"}>
										<SidebarMenuButton className={getMenuItemClass("price-overrides")}>
											<TagIcon />
											Price Overrides
										</SidebarMenuButton>
									</Link>
								</SidebarMenuItem>
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				</SidebarContent>
				<SidebarFooter>
					<SidebarMenu className="mb-4">
						<SidebarMenuItem className="mb-1">
							<Selector
								className="w-full bg-background"
								value={preferredCurrencyValue}
								onChange={onChangePreferredCurrency}
								placeholder="Currency"
								options={CurrencyOptions}
								isLoading={isPreferredCurrencyLoading} />
						</SidebarMenuItem>
						<SidebarMenuItem className="mb-1">
							<Selector
								className="w-full bg-background"
								value={preferredPriceSourceValue}
								onChange={onChangePreferredPriceSource}
								placeholder="Price Source"
								options={PriceSourceOptions}
								isLoading={isPreferredPriceSourceLoading} />
						</SidebarMenuItem>
						{
							screenSizeIsConsideredDesktop(screenSize) && (
								<SidebarMenuItem>
									<OptionBar
										options={ViewTypeOptions}
										value={userPreferencesState.viewTypePreference}
										onChange={dispatchSetViewTypePreference} />
								</SidebarMenuItem>
							)
						}
					</SidebarMenu>
					<SidebarMenu>
						<SidebarMenuItem>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
										{
											emblem.data
												? <Emblem className="w-8 h-8" {...emblem.data} />
												: <Spinner />
										}
										<div className="grid flex-1 text-left text-sm leading-tight">
											<span className="truncate font-medium">{authState.displayName}</span>
											<span className="text-muted-foreground truncate text-xs">
												{authState.emailAddress}
											</span>
										</div>
										<EllipsisVerticalIcon className="ml-auto size-4" />
									</SidebarMenuButton>
								</DropdownMenuTrigger>
								<DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg" side={screenSizeIsConsideredMobile(screenSize) ? "bottom" : "right"} align="end" sideOffset={4}>
									<DropdownMenuGroup>
										<Link to={"/settings/subscription"}>
											<DropdownMenuItem>
												<CrownIcon />
												Subscription
											</DropdownMenuItem>
										</Link>
										<Link to={"/settings/payment-cards"}>
											<DropdownMenuItem>
												<CreditCardIcon />
												Payment Cards
											</DropdownMenuItem>
										</Link>
									</DropdownMenuGroup>
									<DropdownMenuSeparator />
									<DropdownMenuGroup>
										<DropdownMenuItem>
											<BellIcon />
											Notification Settings
										</DropdownMenuItem>
									</DropdownMenuGroup>
									<DropdownMenuSeparator />
									<DropdownMenuGroup>
										<Link to="/settings/change-password">
											<DropdownMenuItem>
												<LockIcon />
												Change Password
											</DropdownMenuItem>
										</Link>
										<Link to="/settings/mfa">
											<DropdownMenuItem>
												<QrCodeIcon />
												Multi-factor Authentication
											</DropdownMenuItem>
										</Link>
									</DropdownMenuGroup>
									<DropdownMenuSeparator />
									<DropdownMenuGroup>
										<DropdownMenuItem onClick={() => dispatchAuthLoggedOut()}>
											<LogOutIcon />
											Logout
										</DropdownMenuItem>
									</DropdownMenuGroup>
								</DropdownMenuContent>
							</DropdownMenu>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarFooter>
				<div className="p-3 border-t text-center text-[0.5rem] text-muted-foreground/60">
					© {dayjs().year()} Fyendal&apos;s Collection. Not affiliated with Legend Story Studios®. All assets and materials related to the Flesh &amp; Blood™ intellectual property belong to Legend Story Studios®.
					<Button variant="ghost" size="sm" className="mt-2 text-xs text-muted-foreground" asChild>
						<a href="https://discord.gg/RDYn8yDnFm" target="_blank" rel="noopener noreferrer">
							<img src={discord} className="h-4 w-4" />
							Join us on Discord
						</a>
					</Button>
				</div>
			</Sidebar>

			<main className="w-full bg-sidebar">
				<div className="flex justify-center p-4 lg:hidden">
					<img src={icon} className="h-6 w-6" />
				</div>
				<div className="@container bg-background lg:h-[calc(100%-(--spacing(3)))] h-[calc(100%-(--spacing(14)))] lg:mt-3 rounded-tl-xl rounded-tr-xl lg:rounded-tr-none">
					<HeaderBarProvider>
						<Outlet />
					</HeaderBarProvider>
				</div>
			</main>
		</SidebarProvider>
	);
};

const CloseMobileSidebarOnNavigate: FC = () => {
	const location = useLocation();
	const { setOpenMobile } = useSidebar();

	useEffect(() => {
		setOpenMobile(false);
	}, [location.key]);
};
